import React, { useEffect } from "react";
import CircularLoading from "../components/CircularLoading";
import { useSnackbar } from '../components/SnackbarProvider.jsx';
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import MaintenanceView from './MaintenanceView';

function AuthLoading(props) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    fetchUser,
    fetchCarTypes,
    fetchSettings,
    fetchBookings,
    fetchCancelReasons,
    fetchPromos,
    fetchDriverEarnings,
    fetchUsers,
    fetchNotifications,
    fetchEarningsReport,
    signOut,
    fetchWithdraws,
    fetchPaymentMethods,
    fetchLanguages,
    fetchWalletHistory,
    fetchCars,
    fetchZoneData
  } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const languagedata = useSelector((state) => state.languagedata);
  const settingsdata = useSelector((state) => state.settingsdata);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch, fetchSettings]);

  useEffect(() => {
    if (languagedata.langlist) {
      // Check for user's saved language preference first
      const storedLang = localStorage.getItem('lang');
      let selectedLang = null;
      let defaultLang = null;

      for (const value of Object.values(languagedata.langlist)) {
        // Add all language bundles
        i18n.addResourceBundle(
          value.langLocale,
          "translations",
          value.keyValuePairs,
          true,   // deep merge
          true    // overwrite defaults so Firebase strings win
        );
        const localeAlias =
          value.langLocale === 'nb' ? 'no' : value.langLocale === 'no' ? 'nb' : null;
        if (localeAlias) {
          i18n.addResourceBundle(localeAlias, "translations", value.keyValuePairs, true, true);
        }

        // Track the default and user-selected languages
        if (value.default === true) {
          defaultLang = value;
        }
        if (storedLang && value.langLocale === storedLang) {
          selectedLang = value;
        }
      }

      // Use stored language if available, otherwise use default
      const langToUse = selectedLang || defaultLang;
      if (langToUse) {
        i18n.changeLanguage(langToUse.langLocale);
        moment.locale(langToUse.dateLocale);
      }

      dispatch(fetchUser());
    }
  }, [languagedata, dispatch, fetchUser]);

  useEffect(() => {
    if (settingsdata.settings) {
      dispatch(fetchLanguages());
      dispatch(fetchCarTypes());
      dispatch(fetchZoneData());
      document.title = settingsdata.settings.appName;
    }
  }, [settingsdata.settings, dispatch, fetchLanguages, fetchCarTypes, fetchZoneData]);

  useEffect(() => {
    if (auth.profile) {
      if (auth.profile.usertype) {
        let role = auth.profile.usertype;
        if (role === "customer") {
          dispatch(fetchBookings());
          dispatch(fetchWalletHistory());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCancelReasons());
          dispatch(fetchUsers());
        } else if (role === "driver") {
          dispatch(fetchBookings());
          dispatch(fetchWithdraws());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchWalletHistory());
        } else if (role === "admin") {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchPromos());
          dispatch(fetchDriverEarnings());
          dispatch(fetchNotifications());
          dispatch(fetchEarningsReport());
          dispatch(fetchCancelReasons());
          dispatch(fetchWithdraws());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchZoneData());
        } else if (role === "fleetadmin") {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchDriverEarnings());
          dispatch(fetchCars());
          dispatch(fetchCancelReasons());
        } else {
          enqueueSnackbar(t('not_valid_user_type'), { variant: 'warning' });
          dispatch(signOut());
        }
      } else {
        enqueueSnackbar(t('user_issue_contact_admin'), { variant: 'warning' });
        dispatch(signOut());
      }
    }
  }, [
    auth.profile,
    dispatch,
    fetchBookings,
    fetchCancelReasons,
    fetchDriverEarnings,
    fetchEarningsReport,
    fetchNotifications,
    fetchPromos,
    fetchUsers,
    fetchWithdraws,
    signOut,
    fetchPaymentMethods,
    fetchWalletHistory,
    fetchCars,
    fetchZoneData,
    t,
    enqueueSnackbar,
  ]);

  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const shouldShowMaintenance = Boolean(
    settingsdata.settings &&
    settingsdata.settings.maintenanceMode &&
    !isAdmin &&
    pathname !== '/login'
  );

  if (!settingsdata.loading && shouldShowMaintenance) {
    return <MaintenanceView message={settingsdata.settings?.maintenanceMessage} />;
  }

  return settingsdata.loading ? (
    <CircularLoading />
  ) : settingsdata.settings ? (
    auth.loading || !languagedata.langlist ? (
      <CircularLoading />
    ) : (
      props.children
    )
  ) : (
    <div>
      <span>No Database Settings found</span>
  </div>
  );
}

export default AuthLoading;
